import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { type ChangeEvent, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { Btn } from '../../components/Btn';
import { Card } from '../../components/Card';
import { PageHeader } from '../../components/PageHeader';
import { listBranches } from '../../data/fetchers';
import { importProductsFromPreview } from '../../data/mutators';
import { canEditMasterData, visibleBranchIds } from '../../lib/permissions';
import { useSession } from '../../stores/session';
import {
  downloadProductImportTemplate,
  parseProductImportWorkbook,
  type ProductImportPreview,
} from './import/productImport';

const readFileAsArrayBuffer = (file: File): Promise<ArrayBuffer> => {
  if (typeof file.arrayBuffer === 'function') {
    return file.arrayBuffer();
  }

  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onerror = () => reject(reader.error);
    reader.onload = () => {
      if (reader.result instanceof ArrayBuffer) {
        resolve(reader.result);
      } else {
        reject(new Error('อ่านไฟล์ Excel ไม่สำเร็จ'));
      }
    };
    reader.readAsArrayBuffer(file);
  });
};

export function ProductImportView() {
  const navigate = useNavigate();
  const qc = useQueryClient();
  const user = useSession((state) => state.user);
  const canEdit = user ? canEditMasterData(user) : false;
  const { data: branches = [] } = useQuery({
    queryKey: ['branches'],
    queryFn: listBranches,
  });
  const allowedBranchIds = user
    ? visibleBranchIds(
        user,
        branches.map((branch) => branch.id),
      )
    : [];
  const visibleBranches = branches.filter((branch) =>
    allowedBranchIds.includes(branch.id),
  );
  const [branchId, setBranchId] = useState('');
  const [preview, setPreview] = useState<ProductImportPreview | null>(null);
  const [parseError, setParseError] = useState('');

  const mutation = useMutation({
    mutationFn: importProductsFromPreview,
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['products'] });
      void qc.invalidateQueries({ queryKey: ['products-page'] });
      void qc.invalidateQueries({ queryKey: ['categories'] });
      void qc.invalidateQueries({ queryKey: ['stock'] });
      void qc.invalidateQueries({ queryKey: ['movements'] });
      navigate('/products');
    },
  });

  const onFileChange = async (event: ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    setParseError('');
    setPreview(null);
    mutation.reset();
    if (!file) return;

    try {
      const buffer = await readFileAsArrayBuffer(file);
      setPreview(await parseProductImportWorkbook(buffer));
    } catch (error) {
      setParseError(
        error instanceof Error ? error.message : 'อ่านไฟล์ Excel ไม่สำเร็จ',
      );
    }
  };

  const canConfirm =
    canEdit && !!branchId && !!preview && preview.errors.length === 0;

  return (
    <div className="bo-fade max-w-6xl mx-auto w-full">
      <PageHeader
        title="นำเข้าสินค้าจาก Excel"
        subtitle="เลือกสาขา อัปโหลดไฟล์ ตรวจสอบตัวอย่าง แล้วค่อยยืนยันนำเข้า"
        actions={
          <Btn
            kind="default"
            type="button"
            onClick={() => void downloadProductImportTemplate()}
          >
            ดาวน์โหลด Template
          </Btn>
        }
      />

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 items-start">
        <Card title="ไฟล์นำเข้า">
          <div className="flex flex-col gap-4">
            <label className="flex flex-col gap-1.5">
              <span className="text-sm-label font-semibold text-ink-2">
                สาขาที่นำเข้าสต๊อก
              </span>
              <select
                aria-label="สาขาที่นำเข้าสต๊อก"
                value={branchId}
                onChange={(event) => setBranchId(event.target.value)}
                disabled={!canEdit}
                className="h-10 px-3 rounded-btn border border-line bg-surface text-body"
              >
                <option value="">เลือกสาขา</option>
                {visibleBranches.map((branch) => (
                  <option key={branch.id} value={branch.id}>
                    {branch.name}
                  </option>
                ))}
              </select>
            </label>

            <label className="flex flex-col gap-1.5">
              <span className="text-sm-label font-semibold text-ink-2">
                ไฟล์ Excel
              </span>
              <input
                aria-label="ไฟล์ Excel"
                type="file"
                accept=".xlsx,.xls"
                disabled={!canEdit}
                onChange={onFileChange}
                className="text-body"
              />
            </label>

            {parseError && (
              <div className="text-danger text-body">{parseError}</div>
            )}
            {mutation.error && (
              <div className="text-danger text-body">
                {mutation.error instanceof Error
                  ? mutation.error.message
                  : 'นำเข้าไม่สำเร็จ'}
              </div>
            )}

            <Btn
              kind="primary"
              type="button"
              disabled={!canConfirm || mutation.isPending}
              onClick={() => {
                if (preview) mutation.mutate({ branchId, preview });
              }}
            >
              ยืนยันนำเข้า
            </Btn>
          </div>
        </Card>

        <div className="lg:col-span-2 flex flex-col gap-4">
          <Card title="ตัวอย่างก่อนนำเข้า">
            {!preview ? (
              <p className="text-body text-ink-2">
                อัปโหลดไฟล์ Excel เพื่อดูตัวอย่าง
              </p>
            ) : (
              <div className="flex flex-col gap-4">
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-body">
                  <div>สินค้า {preview.summary.productCount} รายการ</div>
                  <div>หน่วยขาย {preview.summary.unitCount} หน่วย</div>
                  <div>บาร์โค้ด {preview.summary.barcodeCount} รายการ</div>
                  <div>
                    มีสต๊อกตั้งต้น {preview.summary.openingStockProductCount}{' '}
                    รายการ
                  </div>
                </div>

                {preview.errors.length > 0 && (
                  <div className="rounded border border-danger/30 bg-danger-light p-3">
                    <div className="font-bold text-danger mb-2">
                      ต้องแก้ไขก่อนนำเข้า
                    </div>
                    <ul className="list-disc pl-5 text-body text-ink-2">
                      {preview.errors.map((issue, index) => (
                        <li key={`${issue.code}-${index}`}>{issue.message}</li>
                      ))}
                    </ul>
                  </div>
                )}

                {preview.warnings.length > 0 && (
                  <div className="rounded border border-warning/30 bg-warning-light p-3">
                    <div className="font-bold text-ink mb-2">คำเตือน</div>
                    <ul className="list-disc pl-5 text-body text-ink-2">
                      {preview.warnings.map((issue, index) => (
                        <li key={`${issue.code}-${index}`}>{issue.message}</li>
                      ))}
                    </ul>
                  </div>
                )}

                <div className="overflow-x-auto">
                  <table className="w-full text-body">
                    <thead>
                      <tr className="text-left text-ink-2 border-b border-line">
                        <th className="py-2">สินค้า</th>
                        <th className="py-2">หมวดหมู่</th>
                        <th className="py-2">หน่วย</th>
                        <th className="py-2 text-right">สต๊อกตั้งต้น</th>
                      </tr>
                    </thead>
                    <tbody>
                      {preview.products.map((product) => (
                        <tr
                          key={product.importKey}
                          className="border-b border-line"
                        >
                          <td className="py-2 font-semibold">{product.name}</td>
                          <td className="py-2">{product.categoryName}</td>
                          <td className="py-2">
                            {product.units.map((unit) => unit.unit).join(', ')}
                          </td>
                          <td className="py-2 text-right num">
                            {product.openingStockBaseQty} {product.baseUnit}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </Card>
        </div>
      </div>
    </div>
  );
}
